/**
 * Reported live (Network tab): every poll loop in the console kept firing at full cadence
 * on a backgrounded tab -- confirmed by grepping the whole codebase and finding zero uses
 * of visibilityState/visibilitychange anywhere. A tab nobody is looking at gains nothing
 * from a fetch firing on schedule, but still costs the server the same as a foregrounded
 * one. This wraps the `tick(); setInterval(tick, ms)` shape every poll loop in the console
 * already uses, so each call site becomes a one-line swap instead of hand-rolling the same
 * visibility check and catch-up listener again.
 *
 * Call from inside a useEffect and return its own return value as (part of) that effect's
 * cleanup -- it does not itself integrate with React, so it composes with whatever else
 * the effect's cleanup already needs to do (closing an EventSource, flipping an `alive`
 * flag, etc).
 */
export function pollWhileVisible(tick: () => void, intervalMs: number): () => void {
  const guardedTick = (): void => {
    if (document.visibilityState === 'hidden') return
    tick()
  }
  guardedTick()
  const id = setInterval(guardedTick, intervalMs)
  // Catches up immediately on return instead of leaving the view stale for up to
  // `intervalMs` after refocus -- unguarded, not guardedTick, since visibilitychange only
  // ever fires this while the document IS visible, and checking again here would just be
  // a same-tick race with the browser's own state.
  const onVisible = (): void => { if (document.visibilityState === 'visible') tick() }
  document.addEventListener('visibilitychange', onVisible)
  return () => {
    clearInterval(id)
    document.removeEventListener('visibilitychange', onVisible)
  }
}
